import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
    fetchJobOpeningById, 
    approveJobPosting, 
    rejectJobPosting,
    updateEnhancedDescription,
    selectCurrentJobOpening,
    selectApproveStatus,
    selectRejectStatus,
    selectError,
    clearError
} from '../_store/jobpostingsSlice';

export const AdminJobReview = () => {
    const { jobId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const job = useSelector(selectCurrentJobOpening);
    const approveStatus = useSelector(selectApproveStatus);
    const rejectStatus = useSelector(selectRejectStatus);
    const error = useSelector(selectError);
    
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [enhancedDescription, setEnhancedDescription] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    
    // Admin password for API calls only (not for frontend auth)
    const ADMIN_API_PASSWORD = process.env.REACT_APP_ADMIN_API_PASSWORD || 'admin123';
    
    useEffect(() => {
        validateToken();
    }, [jobId, token]);
    
    useEffect(() => {
        if (isTokenValid && jobId) {
            dispatch(fetchJobOpeningById(jobId));
        }
    }, [isTokenValid, jobId, dispatch]);
    
    useEffect(() => {
        if (job?.enhancedDescription) {
            setEnhancedDescription(job.enhancedDescription);
        }
    }, [job]);
    
    const validateToken = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/validate-review-token/${jobId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });
            
            const data = await response.json();
            
            if (data.valid) {
                setIsTokenValid(true);
            } else {
                alert(data.message || 'Invalid or expired link');
                navigate('/');
            }
        } catch (error) {
            alert('Failed to validate access');
            navigate('/');
        } finally {
            setIsValidating(false);
        }
    };
    
    const handleApprove = async () => {
        if (!window.confirm('Approve this job posting?')) return;
        
        try {
            await dispatch(approveJobPosting({ 
                jobId, 
                adminPassword: ADMIN_API_PASSWORD 
            })).unwrap();
            
            alert('Project approved successfully!');
            navigate('/');
        } catch (error) {
            console.error('Approval failed:', error);
        }
    };
    
    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }
        
        try {
            await dispatch(rejectJobPosting({ 
                jobId, 
                rejectionReason, 
                adminPassword: ADMIN_API_PASSWORD 
            })).unwrap();
            
            alert('Project rejected successfully');
            setShowRejectModal(false);
            navigate('/');
        } catch (error) {
            console.error('Rejection failed:', error);
        }
    };
    
    const handleUpdateDescription = async () => {
        try {
            await dispatch(updateEnhancedDescription({ 
                jobId, 
                enhancedDescription, 
                adminPassword: ADMIN_API_PASSWORD 
            })).unwrap();
            
            setEditingDescription(false);
            alert('Description updated successfully');
        } catch (error) {
            console.error('Update failed:', error);
        }
    };
    
    const styles = {
        container: {
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: '2rem'
        },
        header: {
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        },
        content: {
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        },
        section: {
            marginBottom: '2rem',
            padding: '1.5rem',
            background: '#f8f9fa',
            borderRadius: '8px'
        },
        sectionTitle: {
            fontSize: '1.2rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#333',
            borderBottom: '2px solid #007bff',
            paddingBottom: '0.5rem'
        },
        label: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '0.25rem',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        value: {
            marginBottom: '1rem',
            color: '#333',
            fontSize: '1rem',
            lineHeight: '1.5'
        },
        textarea: {
            width: '100%',
            minHeight: '200px',
            padding: '1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            marginBottom: '1rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginTop: '2rem'
        },
        approveButton: {
            flex: 1,
            padding: '12px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
        },
        rejectButton: {
            flex: 1,
            padding: '12px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
        },
        editButton: {
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
        },
        cancelButton: {
            padding: '8px 16px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
        },
        modal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        },
        modalContent: {
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
        },
        badge: {
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600',
            background: '#ffc107',
            color: '#000'
        },
        loadingContainer: {
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#f5f5f5'
        }
    };
    
    // Loading State
    if (isValidating) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.content}>
                    <h2>Validating access...</h2>
                </div>
            </div>
        );
    }
    
    // Invalid Token
    if (!isTokenValid) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.content}>
                    <h2 style={{ color: '#dc3545' }}>Invalid or Expired Link</h2>
                    <p>This review link is invalid or has expired.</p>
                    <button 
                        onClick={() => navigate('/')}
                        style={styles.approveButton}
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }
    
    // Loading Job
    if (!job) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.content}>
                    <h2>Loading job details...</h2>
                </div>
            </div>
        );
    }
    
    // Main Review Page
    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={{ margin: 0, color: '#333' }}>Review Project Posting</h1>
                <div style={{ marginTop: '10px' }}>
                    <span style={styles.badge}>Pending Review</span>
                    <span style={{ marginLeft: '10px', color: '#666' }}>
                        Project ID: {jobId}
                    </span>
                </div>
            </div>
            
            <div style={styles.content}>
                {/* Job Details Section */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Project Details</h3>
                    
                    <div style={styles.label}>Project Title</div>
                    <div style={styles.value}>{job.name}</div>
                    
                    <div style={styles.label}>Location</div>
                    <div style={styles.value}>{job.location}</div>
                    
                    <div style={styles.label}>Posted By</div>
                    <div style={styles.value}>{job.email}</div>
                    
                    <div style={styles.label}>Contact Number</div>
                    <div style={styles.value}>{job.contactNumber}</div>
                    
                    <div style={styles.label}>Submitted On</div>
                    <div style={styles.value}>
                        {new Date(job.createdAt).toLocaleString()}
                    </div>
                </div>
                
                {/* Original Description */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Original Description</h3>
                    <div style={{...styles.value, whiteSpace: 'pre-wrap'}}>
                        {job.originalDescription}
                    </div>
                </div>
                
                {/* Enhanced Description */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Enhanced Description</h3>
                    
                    {editingDescription ? (
                        <>
                            <textarea
                                value={enhancedDescription}
                                onChange={(e) => setEnhancedDescription(e.target.value)}
                                style={styles.textarea}
                            />
                            <div>
                                <button 
                                    onClick={handleUpdateDescription}
                                    style={styles.editButton}
                                >
                                    Save Changes
                                </button>
                                <button 
                                    onClick={() => {
                                        setEnhancedDescription(job.enhancedDescription);
                                        setEditingDescription(false);
                                    }}
                                    style={styles.cancelButton}
                                >
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{...styles.value, whiteSpace: 'pre-wrap'}}>
                                {enhancedDescription || 'No enhanced description available'}
                            </div>
                            <button 
                                onClick={() => setEditingDescription(true)}
                                style={styles.editButton}
                            >
                                Edit Description
                            </button>
                        </>
                    )}
                </div>
                
                {/* Action Buttons */}
                <div style={styles.buttonGroup}>
                    <button 
                        onClick={handleApprove}
                        style={styles.approveButton}
                        disabled={approveStatus === 'loading'}
                    >
                        {approveStatus === 'loading' ? 'Approving...' : '✓ Approve Project'}
                    </button>
                    <button 
                        onClick={() => setShowRejectModal(true)}
                        style={styles.rejectButton}
                        disabled={rejectStatus === 'loading'}
                    >
                        {rejectStatus === 'loading' ? 'Rejecting...' : '✗ Reject Project'}
                    </button>
                </div>
            </div>
            
            {/* Reject Modal */}
            {showRejectModal && (
                <div style={styles.modal} onClick={() => setShowRejectModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '1rem' }}>Reject Job Posting</h3>
                        <div style={styles.label}>Rejection Reason</div>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Please provide a reason for rejection..."
                            style={{...styles.textarea, minHeight: '100px'}}
                        />
                        <div style={styles.buttonGroup}>
                            <button onClick={handleReject} style={styles.rejectButton}>
                                Confirm Rejection
                            </button>
                            <button 
                                onClick={() => setShowRejectModal(false)} 
                                style={styles.cancelButton}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
